import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { syncedLocationKeys } from './synced-locations';
import type { PreviewSuccessResponse, PreviewErrorResponse } from '@/types/api';

export const publishingKeys = {
  all: ['publishing'] as const,
  preview: (syncedLocationId: string) => [...publishingKeys.all, 'preview', syncedLocationId] as const,
  status: (syncedLocationId: string) => [...publishingKeys.all, 'status', syncedLocationId] as const,
  currencies: () => [...publishingKeys.all, 'currencies'] as const,
};

/** A currency ScoutAround accepts (GET /api/v1/publishing/currencies). */
export interface SupportedCurrency {
  id: number;
  code: string;
  name: string;
}

interface SupportedCurrenciesResponse {
  success: boolean;
  currencies?: SupportedCurrency[];
}

/**
 * The currencies publishing accepts. The Bridge backend reads them from the
 * ScoutAround backend's currency table (falling back to EUR and USD itself),
 * so this list is what publish validates against.
 */
export function useSupportedCurrencies() {
  return useQuery({
    queryKey: publishingKeys.currencies(),
    queryFn: async () => {
      const response = await api.get<SupportedCurrenciesResponse>('/api/v1/publishing/currencies');
      return response.currencies ?? [];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes - the backend caches the list for an hour
  });
}

type PreviewResponse = PreviewSuccessResponse | PreviewErrorResponse;

export interface PublishSuccessResponse {
  success: true;
  publishedLocation: {
    id: string;
    syncedLocationId: string;
    scoutaroundId: string;
    scoutaroundUrl: string;
    publishedAt: string;
  };
}

export interface ValidationError {
  field: string;
  message: string;
  overridable: boolean;
}

export interface PublishErrorResponse {
  success: false;
  error: string;
  availableCurrencies?: string[];
  validationErrors?: ValidationError[];
  isForcePublishable?: boolean;
}

export interface UnpublishResponse {
  success: boolean;
  error?: string;
}

export interface PublicationStatusResponse {
  success: true;
  isPublished: boolean;
  scoutaroundId?: string;
  scoutaroundUrl?: string;
  publishedAt?: string;
  publishedBy?: number;
}

export interface SyncEnrichmentSuccessResponse {
  success: true;
  syncedLocationId: string;
  scoutaroundId: string;
  syncedAt: string;
  updatedFields: string[];
}

export interface SyncEnrichmentErrorResponse {
  success: false;
  error?: string;
}

export function usePreview(syncedLocationId: string, enabled = true) {
  return useQuery({
    queryKey: publishingKeys.preview(syncedLocationId),
    queryFn: async () => {
      const response = await api.get<PreviewResponse>(`/api/v1/publishing/${syncedLocationId}/preview`);
      return response;
    },
    enabled: enabled && !!syncedLocationId,
    retry: false,
    staleTime: 0, // Always refetch to get latest preview data
  });
}

export function usePublicationStatus(syncedLocationId: string, enabled = true) {
  return useQuery({
    queryKey: publishingKeys.status(syncedLocationId),
    queryFn: async () => {
      const response = await api.get<PublicationStatusResponse>(`/api/v1/publishing/${syncedLocationId}/status`);
      return response;
    },
    enabled: enabled && !!syncedLocationId,
    retry: false,
  });
}

interface PublishInput {
  syncedLocationId: string;
  force?: boolean;
}

export function usePublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ syncedLocationId, force }: PublishInput) => {
      const response = await api.post<PublishSuccessResponse | PublishErrorResponse>(
        `/api/v1/publishing/${syncedLocationId}/publish`,
        force ? { force: true } : {}
      );
      return response;
    },
    onSuccess: (_, { syncedLocationId }) => {
      void queryClient.invalidateQueries({ queryKey: publishingKeys.status(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: publishingKeys.preview(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

/** The admin's optional coded reason and note travel with the unpublish (FR-178). */
export interface UnpublishInput {
  syncedLocationId: string;
  reasonCode: string | null;
  note: string | null;
}

export function useUnpublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ syncedLocationId, reasonCode, note }: UnpublishInput) => {
      const response = await api.post<UnpublishResponse>(
        `/api/v1/publishing/${syncedLocationId}/unpublish`,
        { ...(reasonCode === null ? {} : { reasonCode }), ...(note === null ? {} : { note }) }
      );
      return response;
    },
    onSuccess: (_, { syncedLocationId }) => {
      void queryClient.invalidateQueries({ queryKey: publishingKeys.status(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: publishingKeys.preview(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

export function useSyncEnrichment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (syncedLocationId: string) => {
      const response = await api.post<SyncEnrichmentSuccessResponse | SyncEnrichmentErrorResponse>(
        `/api/v1/publishing/${syncedLocationId}/sync`,
        {}
      );
      return response;
    },
    onSuccess: (_, syncedLocationId) => {
      void queryClient.invalidateQueries({ queryKey: publishingKeys.status(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: publishingKeys.preview(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}
